import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@lib/utils';
import { useCallback, useMemo, useState } from 'react';
import isPromise from 'is-promise';
import { Spinner } from '@features/ui';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 text-white text-sm font-medium',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'border border-input shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-[#4DA2FF] text-secondary-foreground shadow-xs hover:bg-[#4DA2FFCC]',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        gradient:
          'bg-gradient-to-r from-tertiary to-primary text-white text-base font-medium',
        lime: 'bg-lime shadow-xs hover:bg-lime/90 focus-visible:ring-lime/20 dark:focus-visible:ring-lime/40 text-white',
        yellow:
          'bg-yellow shadow-xs hover:bg-yellow/90 focus-visible:ring-yellow/20 dark:focus-visible:ring-yellow/40 text-white',
        black:
          'bg-background border-[1px] border-[white] shadow-xs hover:bg-background/90 focus-visible:ring-background/20 dark:focus-visible:ring-background/40 text-white',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: 'h-6.5 rounded-[5px] px-1 has-[>svg]:px-2.5',
        sm: 'h-7.5 rounded-[2px] px-3 has-[>svg]:px-2.5 text-xs',
        lg: 'h-11 rounded-lg px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  loading,
  onClick,
  disabled,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const [processing, setProcessing] = useState(false);

  const _disabled = useMemo(
    () => disabled || processing || loading,
    [disabled, loading, processing]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!onClick) {
        return;
      }
      const pressed = onClick(e) as void | Promise<void>;

      if (isPromise(pressed)) {
        setProcessing(true);
        pressed.finally(() => setProcessing(false));
      }
    },
    [onClick]
  );

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        'cursor-pointer',
        buttonVariants({ variant, size, className })
      )}
      data-slot="button"
      disabled={_disabled}
      onClick={handleClick}
      {...props}
    >
      {children}
      {(loading || processing) && <Spinner />}
    </Comp>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
